import { CURRENT_LANGUAGE_CODE } from './language.js';
import { getPhoneCountry } from './phoneCountry.js';

/**
 * Модуль для работы с маской телефона
 * Использует библиотеку intlTelInput для международного форматирования номеров
 */

const getIntlTelInputUtils = () => window.intlTelInput?.utils || null;
const utilsLoadPromises = new WeakMap();
const loadIntlTelInputUtils = () => import('https://cdn.jsdelivr.net/npm/intl-tel-input@25.3.0/build/js/utils.js');

const ensureIntlTelInputUtils = () => {
  const intlTelInput = window.intlTelInput;
  if (intlTelInput.utils) return Promise.resolve(intlTelInput.utils);

  const pendingPromise = utilsLoadPromises.get(intlTelInput);
  if (pendingPromise) return pendingPromise;

  const attachPromise = intlTelInput.attachUtils(loadIntlTelInputUtils);
  const loadPromise = Promise.resolve(attachPromise || intlTelInput.utils).catch((error) => {
    utilsLoadPromises.delete(intlTelInput);
    throw error;
  });

  utilsLoadPromises.set(intlTelInput, loadPromise);
  return loadPromise;
};

export class PhoneMask {
  constructor(inputElement, options = {}) {
    this.phoneInput = inputElement;
    this.wrapper = inputElement.closest('.js-phone-input-wrapper');
    this.iti = null;
    this.phoneMaxLength = 15;
    
    const browserLanguages = typeof navigator === 'undefined'
      ? []
      : (navigator.languages?.length ? navigator.languages : [navigator.language]);
    const supportedCountries = window.intlTelInput.getCountryData?.().map(({ iso2 }) => iso2) || [];
    const country = getPhoneCountry(browserLanguages, CURRENT_LANGUAGE_CODE, supportedCountries);

    // A saved international number lets intl-tel-input select its own country.
    this.options = {
      initialCountry: /^\+\d/.test(inputElement.value.trim()) ? '' : country,
      language: CURRENT_LANGUAGE_CODE === 'en' ? 'us' : CURRENT_LANGUAGE_CODE,
      showValidation: options.showValidation !== false,
      noCountrySelectedLabel: inputElement.dataset?.noCountryLabel || 'Select country'
    };
    
    // Сообщения об ошибках
    this.errorMessages = {
      ru: {
        REQUIRED: 'Обязательное поле',
        INCORRECT_PHONE: 'Некорректный телефон',
        TOO_SHORT: 'Номер слишком короткий',
        TOO_LONG: 'Номер слишком длинный',
        INVALID_COUNTRY_CODE: 'Неверный код страны',
        INVALID_LENGTH: 'Неверная длина номера'
      },
      us: {
        REQUIRED: 'Required',
        INCORRECT_PHONE: 'Phone incorrect',
        TOO_SHORT: 'Number too short',
        TOO_LONG: 'Number too long',
        INVALID_COUNTRY_CODE: 'Invalid country code',
        INVALID_LENGTH: 'Invalid number length'
      },
      es: {
        REQUIRED: 'Campo obligatorio',
        INCORRECT_PHONE: 'Teléfono incorrecto',
        TOO_SHORT: 'Número demasiado corto',
        TOO_LONG: 'Número demasiado largo',
        INVALID_COUNTRY_CODE: 'Código de país incorrecto',
        INVALID_LENGTH: 'Longitud de número incorrecta'
      },
      pt: {
        REQUIRED: 'Campo obrigatório',
        INCORRECT_PHONE: 'Telefone incorreto',
        TOO_SHORT: 'Número muito curto',
        TOO_LONG: 'Número muito longo',
        INVALID_COUNTRY_CODE: 'Código do país incorreto',
        INVALID_LENGTH: 'Comprimento do número incorreto'
      },
      br: {
        REQUIRED: 'Campo obrigatório',
        INCORRECT_PHONE: 'Telefone incorreto',
        TOO_SHORT: 'Número muito curto',
        TOO_LONG: 'Número muito longo',
        INVALID_COUNTRY_CODE: 'Código do país incorreto',
        INVALID_LENGTH: 'Comprimento do número incorreto'
      }
    };
    
    this.init();
  }
  
  init() {
    if (!this.phoneInput) return;

    // intlTelInput откладывает loadUtils до window.load. Запускаем загрузку сразу,
    // чтобы она не ждала медленные изображения и внешние скрипты страницы.
    ensureIntlTelInputUtils().catch(() => {});

    // Инициализируем intlTelInput
    this.iti = window.intlTelInput(this.phoneInput, {
      initialCountry: this.options.initialCountry,
      separateDialCode: true,
      autoFormat: true,
      formatAsYouType: true,
      autoPlaceholder: 'off',
      // В v25.3.0 strictMode падает на keydown, пока utils еще не загрузились.
      // Допустимые клавиши уже ограничиваются собственным handleKeydown ниже.
      strictMode: false,
      showSelectedDialCode: true,
      allowDropdown: true,
      i18n: {
        noCountrySelected: this.options.noCountrySelectedLabel
      }
    });
    
    // Привязываем обработчики событий
    this.bindEvents();
  }
  
  bindEvents() {
    this.phoneInput.addEventListener('input', () => this.handleInput());
    this.phoneInput.addEventListener('keydown', (e) => this.handleKeydown(e));
    this.phoneInput.addEventListener('countrychange', () => this.handleCountryChange());
  }
  
  handleInput() {
    if (!this.iti || !this.options.showValidation) return;
    
    // Убираем визуальную обратную связь, оставляем только очистку ошибок для валидных номеров
    if (this.phoneInput.value.trim() !== '' && this.iti.isValidNumber()) {
      this.clearError();
    }
  }
  
  handleKeydown(event) {
    const allowedKeys = [
      8, 9, 27, 13, // backspace, tab, escape, enter
      46, // delete
      35, 36, // home, end
      37, 38, 39, 40, // arrow keys
      32, // space
      43, 45, // +, -
      40, 41, // ( )
    ];
    
    // Allow: Ctrl/Cmd + A, C, V, X, Z
    if (event.ctrlKey || event.metaKey) {
      if ([65, 67, 86, 88, 90].includes(event.keyCode)) {
        return;
      }
    }
    
    // Allow special keys
    if (allowedKeys.includes(event.keyCode)) {
      return;
    }
    
    // Allow numbers
    if ((event.keyCode >= 48 && event.keyCode <= 57) || 
        (event.keyCode >= 96 && event.keyCode <= 105)) {
      return;
    }
    
    event.preventDefault();
  }
  
  handleCountryChange() {
    this.clearError();
  }
  
  validate(showError = false) {
    if (!this.phoneInput.value.trim()) {
      if (showError) {
        this.showError(this.errorMessages[this.options.language].REQUIRED);
      }
      return false;
    }
    
    if (!this.iti) {
      // Fallback validation
      const digitsOnly = this.phoneInput.value.replace(/\D/g, "");
      const isValid = digitsOnly.length >= 5 && digitsOnly.length <= 15;
      
      if (!isValid && showError) {
        this.showError(this.errorMessages[this.options.language].INCORRECT_PHONE);
      }
      
      return isValid;
    }
    
    const isValid = this.iti.isValidNumber();
    
    if (!isValid && showError) {
      const errorCode = this.iti.getValidationError();
      let errorMessage = this.errorMessages[this.options.language].INCORRECT_PHONE;
      
      const utils = getIntlTelInputUtils();
      if (utils) {
        const errorMap = {
          [utils.validationError.TOO_SHORT]:
            this.errorMessages[this.options.language].TOO_SHORT,
          [utils.validationError.TOO_LONG]:
            this.errorMessages[this.options.language].TOO_LONG,
          [utils.validationError.INVALID_COUNTRY_CODE]:
            this.errorMessages[this.options.language].INVALID_COUNTRY_CODE,
          [utils.validationError.INVALID_LENGTH]:
            this.errorMessages[this.options.language].INVALID_LENGTH
        };
        
        errorMessage = errorMap[errorCode] || errorMessage;
      }
      
      this.showError(errorMessage);
    }
    
    return isValid;
  }
  
  getFormattedNumber() {
    return this.iti ? this.iti.getNumber() : this.phoneInput.value;
  }
  
  showError(errorText) {
    if (!this.wrapper) return;
    
    this.wrapper.classList.add("form-info__error");
    
    if (errorText) {
      const inputField = this.wrapper.closest('.form-info__input-field');
      if (inputField) {
        const errorItem = inputField.querySelector('.form-info__error-field');
        if (errorItem) {
          errorItem.classList.add('form-info__error-field_visible');
          errorItem.textContent = errorText;
        }
      }
    }
  }
  
  clearError() {
    if (!this.wrapper) return;
    
    this.wrapper.classList.remove("form-info__error");
    
    const inputField = this.wrapper.closest('.form-info__input-field');
    if (inputField) {
      const errorItem = inputField.querySelector('.form-info__error-field');
      if (errorItem) {
        errorItem.classList.remove('form-info__error-field_visible');
        errorItem.textContent = '';
      }
    }
  }
  
  destroy() {
    if (this.iti) {
      this.iti.destroy();
      this.iti = null;
    }
  }
}

// Функция для быстрой инициализации
export function initPhoneMask(inputElement, options = {}) {
  return new PhoneMask(inputElement, options);
}
